import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISSED_KEY = "gio_install_prompt_dismissed_at";
const DISMISS_DAYS = 7;

function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
  return { iOS, isStandalone };
}

/** Install prompt — uses native beforeinstallprompt on Chrome/Edge and a manual
 *  add-to-home-screen guide on iOS Safari (required for web push on iOS 16.4+). */
export default function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    // Respect recent dismissal
    const last = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    const freshAgain = Date.now() - last > DISMISS_DAYS * 86400_000;
    if (!freshAgain) return;

    const { iOS, isStandalone } = isIOSSafari();
    if (isStandalone) return; // already installed

    if (iOS) {
      setIosMode(true);
      // Small delay so the prompt doesn't pop immediately on first load
      const t = setTimeout(() => setVisible(true), 6000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    if (outcome === "accepted") setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed left-1/2 bottom-4 z-40 -translate-x-1/2 w-[92vw] max-w-md px-4 pointer-events-none"
      data-testid="install-prompt">
      <div className="pointer-events-auto rounded-2xl p-4 shadow-2xl"
        style={{
          background: "linear-gradient(135deg, #0A0F1A 0%, #0F1A2E 60%, #14B8A615 100%)",
          border: "1px solid #14B8A640",
          boxShadow: "0 10px 40px -10px rgba(20,184,166,0.4)",
        }}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(135deg,#14B8A6,#06B6D4)" }}>
            <Download size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            {iosMode ? (
              <>
                <div className="text-[14px] font-bold" style={{ color: "#fff" }}>Install GP App on iPhone</div>
                <div className="text-[12px] mt-1" style={{ color: "#cbd5e1" }}>
                  Tap <Share size={11} className="inline -mt-0.5 mx-0.5" style={{ color: "#14B8A6" }} /> <strong>Share</strong> in Safari → scroll down → <strong>"Add to Home Screen"</strong>. Enables push notifications.
                </div>
              </>
            ) : (
              <>
                <div className="text-[14px] font-bold" style={{ color: "#fff" }}>Install GP App</div>
                <div className="text-[12px] mt-1" style={{ color: "#cbd5e1" }}>
                  One-tap open from your home screen · offline access · push notifications
                </div>
                <button onClick={install} data-testid="install-prompt-install"
                  className="btn-primary text-[12px] mt-3 inline-flex items-center gap-1.5">
                  <Download size={12} /> Install app
                </button>
              </>
            )}
          </div>
          <button onClick={dismiss} data-testid="install-prompt-dismiss"
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 shrink-0"
            style={{ color: "#9ca3af" }}>
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
